import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  inject,
  linkedSignal,
  resource,
  signal,
  viewChild,
} from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import type { OrderByDirection, QueryDocumentSnapshot } from '@angular/fire/firestore';
import { SortDirectionToggle } from '../../../shared/components/sort-direction-toggle/sort-direction-toggle';
import { CurrentUserService } from '../../../shared/services/current-user.service';
import { CUSTOMERS_PAGE_SIZE, CustomerApi } from '../customer-api';
import type { Customer } from '../customer';
import { customerSortOption, type CustomerSort } from '../customer-sort';
import { CustomerItem } from '../customer-item/customer-item';
import { CustomerSortSelect } from '../customer-sort-select/customer-sort-select';

/** Paginated list of the current company's customers. */
@Component({
  selector: 'app-customer-list',
  imports: [CustomerItem, CustomerSortSelect, DecimalPipe, RouterLink, SortDirectionToggle],
  templateUrl: './customer-list.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block h-full' },
})
export class CustomerList {
  readonly #customerApi = inject(CustomerApi);
  readonly #currentUser = inject(CurrentUserService);

  readonly #companyId = computed(() => this.#currentUser.company()?.companyId);

  protected readonly sort = signal<CustomerSort>('lastOrderAt');
  protected readonly sortOption = computed(() => customerSortOption(this.sort()));
  /** Resets to the field's natural direction whenever another field is chosen. */
  protected readonly direction = linkedSignal<OrderByDirection>(() => this.sortOption().direction);

  /** What the visited pages belong to; a change starts again from page 1. */
  readonly #query = computed(() => ({
    companyId: this.#companyId(),
    sort: this.sort(),
    direction: this.direction(),
  }));

  // Start cursor of every page visited so far (index = page), so Previous needs no extra query.
  // Both reset when the company or the order changes.
  readonly #cursors = linkedSignal({
    source: this.#query,
    computation: (): readonly (QueryDocumentSnapshot<Customer> | null)[] => [null],
  });
  protected readonly pageIndex = linkedSignal({ source: this.#query, computation: () => 0 });

  protected readonly page = resource({
    params: () => {
      const { companyId, sort, direction } = this.#query();
      return companyId
        ? { companyId, order: { sort, direction }, after: this.#cursors()[this.pageIndex()] ?? null }
        : undefined;
    },
    loader: ({ params }) => this.#customerApi.getPage(params.companyId, params.order, params.after),
  });

  protected readonly total = resource({
    params: () => this.#companyId(),
    loader: ({ params: companyId }) => this.#customerApi.count(companyId),
  });

  protected readonly customers = computed(() => (this.page.hasValue() ? this.page.value().customers : []));
  protected readonly hasPrevious = computed(() => this.pageIndex() > 0);
  protected readonly hasNext = computed(() => this.page.hasValue() && this.page.value().hasMore);
  protected readonly pageCount = computed(() =>
    this.total.hasValue() ? Math.max(1, Math.ceil(this.total.value() / CUSTOMERS_PAGE_SIZE)) : null,
  );
  /** 1-based positions of the first and last customer on this page, e.g. 26–50. */
  protected readonly range = computed(() => {
    const count = this.customers().length;
    const first = this.pageIndex() * CUSTOMERS_PAGE_SIZE + 1;
    return count ? { first, last: first + count - 1 } : null;
  });

  protected readonly skeletonRows = Array.from({ length: 6 }, (_, index) => index);

  private readonly scroller = viewChild.required<ElementRef<HTMLElement>>('scroller');

  protected next(): void {
    if (!this.hasNext() || this.page.isLoading()) return;
    const { cursor } = this.page.value()!;
    const nextIndex = this.pageIndex() + 1;
    this.#cursors.update((cursors) => [...cursors.slice(0, nextIndex), cursor]);
    this.#goTo(nextIndex);
  }

  protected previous(): void {
    if (!this.hasPrevious() || this.page.isLoading()) return;
    this.#goTo(this.pageIndex() - 1);
  }

  protected sortBy(sort: CustomerSort): void {
    this.sort.set(sort);
    this.#scrollToTop();
  }

  protected reverse(direction: OrderByDirection): void {
    this.direction.set(direction);
    this.#scrollToTop();
  }

  #goTo(index: number): void {
    this.pageIndex.set(index);
    this.#scrollToTop();
  }

  #scrollToTop(): void {
    this.scroller().nativeElement.scrollTo({ top: 0 });
  }
}
